import React, { useEffect, useMemo, useState } from "react";

import { useDispatch, useSelector } from "react-redux";

import { useHistory } from "react-router-dom";

import {
  FiArrowRight,
  FiBarChart2,
  FiCalendar,
  FiBriefcase,
  FiCheckCircle,
  FiClock,
  FiExternalLink,
  FiHeadphones,
  FiMonitor,
  FiRefreshCw,
  FiServer,
  FiShoppingBag,
  FiSlash,
  FiTrendingUp,
  FiUserPlus,
  FiUsers,
  FiWifi,
  FiXCircle,
} from "react-icons/fi";

import {
  fetchPlatformDashboard,
  selectPlatformDashboard,
  selectPlatformDashboardLoading,
  selectPlatformDashboardError,
} from "../../../../store/platformDashboard/platformDashboardSlice";

import {
  BusinessStatusBadge,
  BusinessFeatureTags,
} from "./business/components";

import "./Platform.css";
import { usePlatformLayout } from "../../../ContextApi/PlatformLayoutContext";
/*
|--------------------------------------------------------------------------
| FORMAT NUMBER
|--------------------------------------------------------------------------
*/

const formatNumber = (value) => {
  return Number(value || 0).toLocaleString("en-IN");
};

/*
|--------------------------------------------------------------------------
| FORMAT DATE
|--------------------------------------------------------------------------
*/

const formatDate = (value) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",

    month: "short",

    year: "numeric",
  });
};

/*
|--------------------------------------------------------------------------
| PLATFORM DASHBOARD
|--------------------------------------------------------------------------
*/

const PlatformDashboard = () => {
  const dispatch = useDispatch();

  const history = useHistory();

  const { setPlatformHeader, resetPlatformHeader } = usePlatformLayout();
  /*
        |--------------------------------------------------------------------------
        | REDUX
        |--------------------------------------------------------------------------
        */

  const dashboard = useSelector(selectPlatformDashboard);

  const loading = useSelector(selectPlatformDashboardLoading);

  const error = useSelector(selectPlatformDashboardError);

  /*
        |--------------------------------------------------------------------------
        | DASHBOARD DATA
        |--------------------------------------------------------------------------
        */

  const summary = dashboard?.summary || {};

  const businesses = summary?.businesses || {};

  const users = summary?.users || {};

  const features = summary?.features || {};

  const periodMetrics = summary?.periodMetrics || {};

  const leadMetrics = periodMetrics?.leads || {};

  const periodRange = periodMetrics?.range || {};

  const recentBusinesses = Array.isArray(dashboard?.recentBusinesses)
    ? dashboard.recentBusinesses
    : [];

  const businessGrowth = Array.isArray(dashboard?.businessGrowth)
    ? dashboard.businessGrowth
    : [];

  const subscriptionSummary = Array.isArray(dashboard?.subscriptionSummary)
    ? dashboard.subscriptionSummary
    : [];

  /*
        |--------------------------------------------------------------------------
        | DASHBOARD PERIOD FILTER
        |--------------------------------------------------------------------------
        */

  const [selectedPeriod, setSelectedPeriod] = useState("today");

  const [customFromDate, setCustomFromDate] = useState("");

  const [customToDate, setCustomToDate] = useState("");

  const [appliedPeriod, setAppliedPeriod] = useState({
    period: "today",
  });

  /*
        |--------------------------------------------------------------------------
        | LOAD
        |--------------------------------------------------------------------------
        */

  useEffect(() => {
    dispatch(fetchPlatformDashboard(appliedPeriod));
  }, [dispatch, appliedPeriod]);

  /*
        |--------------------------------------------------------------------------
        | GROWTH MAX
        |--------------------------------------------------------------------------
        */

  const growthMax = useMemo(() => {
    return Math.max(
      ...businessGrowth.map((item) => Number(item?.total || 0)),
      1,
    );
  }, [businessGrowth]);

  /*
        |--------------------------------------------------------------------------
        | SUBSCRIPTION TOTAL
        |--------------------------------------------------------------------------
        */

  const subscriptionTotal = useMemo(() => {
    return subscriptionSummary.reduce(
      (total, item) => total + Number(item?.total || 0),
      0,
    );
  }, [subscriptionSummary]);

  /*
        |--------------------------------------------------------------------------
        | REFRESH
        |--------------------------------------------------------------------------
        */

  const handleRefresh = () => {
    dispatch(fetchPlatformDashboard(appliedPeriod));
  };

  /*
        |--------------------------------------------------------------------------
        | PERIOD CHANGE
        |--------------------------------------------------------------------------
        */

  const handlePeriodChange = (event) => {
    const value = event.target.value;

    setSelectedPeriod(value);

    if (value !== "custom") {
      setAppliedPeriod({
        period: value,
      });
    }
  };

  /*
        |--------------------------------------------------------------------------
        | APPLY CUSTOM RANGE
        |--------------------------------------------------------------------------
        */

  const handleApplyCustomRange = () => {
    if (!customFromDate || !customToDate) {
      return;
    }

    setAppliedPeriod({
      period: "custom",
      fromDate: customFromDate,
      toDate: customToDate,
    });
  };

  /*
        |--------------------------------------------------------------------------
        | BUSINESS
        |--------------------------------------------------------------------------
        */

  const goToBusinesses = () => {
    history.push("/platform/businesses");
  };


  /*
  |--------------------------------------------------------------------------
  | DASHBOARD CARD NAVIGATION
  |--------------------------------------------------------------------------
  */

  const goToRoute = (path) => {
    if (!path) {
      return;
    }

    history.push(path);
  };

  const clickableCardProps = (path) => ({
    role: "button",
    tabIndex: 0,
    onClick: () => goToRoute(path),
    onKeyDown: (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        goToRoute(path);
      }
    },
  });

  /*
        |--------------------------------------------------------------------------
        | HEADER ACTION
        |--------------------------------------------------------------------------
        */

  const headerActions = (
    <button
      type="button"
      className="platform-refresh-button"
      onClick={handleRefresh}
      disabled={loading}
    >
      <FiRefreshCw className={loading ? "platform-spinning" : ""} />
      Refresh
    </button>
  );
  useEffect(() => {
    setPlatformHeader({
      title: "Platform Dashboard",

      description:
        "Monitor businesses, subscriptions and platform activity from one place.",

      headerActions: headerActions,
    });

    return () => {
      resetPlatformHeader();
    };
  }, [loading]);

  /*
        |--------------------------------------------------------------------------
        | INITIAL LOADING
        |--------------------------------------------------------------------------
        */

  if (loading && !dashboard) {
    return (
      <div className="platform-page-loader">
        <div className="platform-page-loader-logo">
          <FiMonitor />
        </div>

        <div className="platform-page-loader-spinner" />

        <strong>Loading Platform Dashboard</strong>

        <span>Fetching ComKwik platform information...</span>
      </div>
    );
  }

  /*
        |--------------------------------------------------------------------------
        | UI
        |--------------------------------------------------------------------------
        */

  return (
      <div className="platform-dashboard-page">
        {/*
                    |--------------------------------------------------------------------------
                    | ERROR
                    |--------------------------------------------------------------------------
                    */}

        {error ? (
          <div className="platform-error-box">
            <FiXCircle />

            <div>
              <strong>Unable to load platform dashboard</strong>

              <span>{error?.message || "Please try again."}</span>
            </div>

            <button type="button" onClick={handleRefresh}>
              Retry
            </button>
          </div>
        ) : null}

        {/*
                    |--------------------------------------------------------------------------
                    | PERIOD PERFORMANCE
                    |--------------------------------------------------------------------------
                    */}

        <section className="platform-period-performance">
          <div className="platform-period-heading">
            <div>
              <span className="platform-panel-eyebrow">PERFORMANCE OVERVIEW</span>

              <h2>{periodRange?.label || "Today"}</h2>

              <p>
                Leads, support requests, registrations and business growth for the selected period.
              </p>
            </div>

            <div className="platform-period-controls">
              <div className="platform-period-select-wrapper">
                <FiCalendar />

                <select value={selectedPeriod} onChange={handlePeriodChange}>
                  <option value="today">Today</option>
                  <option value="yesterday">Yesterday</option>
                  <option value="this_week">This Week</option>
                  <option value="last_week">Last Week</option>
                  <option value="this_month">This Month</option>
                  <option value="last_month">Last Month</option>
                  <option value="custom">Custom Date</option>
                </select>
              </div>

              {selectedPeriod === "custom" ? (
                <div className="platform-custom-date-range">
                  <input
                    type="date"
                    value={customFromDate}
                    onChange={(event) => setCustomFromDate(event.target.value)}
                  />

                  <span>to</span>

                  <input
                    type="date"
                    value={customToDate}
                    onChange={(event) => setCustomToDate(event.target.value)}
                  />

                  <button
                    type="button"
                    onClick={handleApplyCustomRange}
                    disabled={!customFromDate || !customToDate}
                  >
                    Apply
                  </button>
                </div>
              ) : null}
            </div>
          </div>

          <div className="platform-period-summary-grid">
            <article
              className="platform-period-summary-card platform-clickable-card"
              {...clickableCardProps("/platform/business-partner-leads")}
              title="Open lead management"
            >
              <div className="platform-period-summary-icon">
                <FiTrendingUp />
              </div>
              <span>TOTAL LEADS</span>
              <strong>{formatNumber(leadMetrics?.total)}</strong>
              <small>{periodRange?.label || "Today"}</small>
            </article>

            <article
              className="platform-period-summary-card platform-clickable-card"
              {...clickableCardProps("/platform/business-partner-leads")}
              title="Open Business Partner Leads"
            >
              <div className="platform-period-summary-icon">
                <FiBriefcase />
              </div>
              <span>BUSINESS PARTNER LEADS</span>
              <strong>{formatNumber(leadMetrics?.businessPartner)}</strong>
              <small>Business enquiries</small>
            </article>

            <article
              className="platform-period-summary-card platform-clickable-card"
              {...clickableCardProps("/platform/sell-device-leads")}
              title="Open Sell Device Leads"
            >
              <div className="platform-period-summary-icon">
                <FiMonitor />
              </div>
              <span>SELL DEVICE LEADS</span>
              <strong>{formatNumber(leadMetrics?.sellDevice)}</strong>
              <small>Devices submitted</small>
            </article>

            <article
              className="platform-period-summary-card platform-clickable-card"
              {...clickableCardProps("/platform/support")}
              title="Open Support Tickets"
            >
              <div className="platform-period-summary-icon">
                <FiHeadphones />
              </div>
              <span>SUPPORT REQUESTS</span>
              <strong>{formatNumber(periodMetrics?.supportTickets)}</strong>
              <small>{periodRange?.label || "Today"} support tickets</small>
            </article>

            <article
              className="platform-period-summary-card platform-clickable-card"
              {...clickableCardProps("/platform/tenant-users")}
              title="Open Tenant Users"
            >
              <div className="platform-period-summary-icon">
                <FiUserPlus />
              </div>
              <span>REGISTERED USERS</span>
              <strong>{formatNumber(periodMetrics?.registeredUsers)}</strong>
              <small>Tenant users registered</small>
            </article>

            <article
              className="platform-period-summary-card platform-clickable-card"
              {...clickableCardProps("/platform/businesses")}
              title="Open Businesses"
            >
              <div className="platform-period-summary-icon">
                <FiTrendingUp />
              </div>
              <span>NEW BUSINESSES</span>
              <strong>{formatNumber(periodMetrics?.registeredBusinesses)}</strong>
              <small>Businesses registered</small>
            </article>
          </div>
        </section>

        {/*
                    |--------------------------------------------------------------------------
                    | BUSINESS SUMMARY
                    |--------------------------------------------------------------------------
                    */}

        <section className="platform-summary-grid">
          <article
            className="platform-summary-card platform-clickable-card"
            {...clickableCardProps("/platform/businesses")}
            title="Open Businesses"
          >
            <div className="platform-summary-card-top">
              <div className="platform-summary-icon">
                <FiBriefcase />
              </div>

              <span className="platform-summary-label">TOTAL BUSINESSES</span>
            </div>

            <div className="platform-summary-value">
              {formatNumber(businesses?.total)}
            </div>

            <div className="platform-summary-footer">
              <span className="platform-positive">
                <FiCheckCircle />
                {formatNumber(businesses?.active)} active
              </span>

              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  goToBusinesses();
                }}
              >
                View all
                <FiArrowRight />
              </button>
            </div>
          </article>

          <article
            className="platform-summary-card platform-clickable-card"
            {...clickableCardProps("/platform/tenant-users")}
            title="Open Tenant Users"
          >
            <div className="platform-summary-card-top">
              <div className="platform-summary-icon">
                <FiUsers />
              </div>

              <span className="platform-summary-label">TENANT USERS</span>
            </div>

            <div className="platform-summary-value">
              {formatNumber(users?.total)}
            </div>

            <div className="platform-summary-footer">
              <span className="platform-positive">
                <FiCheckCircle />
                {formatNumber(users?.active)} active users
              </span>
            </div>
          </article>

          <article
            className="platform-summary-card platform-clickable-card"
            {...clickableCardProps("/platform/subscriptions")}
            title="Open Subscriptions"
          >
            <div className="platform-summary-card-top">
              <div className="platform-summary-icon">
                <FiClock />
              </div>

              <span className="platform-summary-label">TRIAL BUSINESSES</span>
            </div>

            <div className="platform-summary-value">
              {formatNumber(businesses?.trial)}
            </div>

            <div className="platform-summary-footer">
              <span>Current trial subscriptions</span>
            </div>
          </article>

          <article
            className="platform-summary-card platform-clickable-card"
            {...clickableCardProps("/platform/businesses")}
            title="Open Businesses"
          >
            <div className="platform-summary-card-top">
              <div className="platform-summary-icon">
                <FiSlash />
              </div>

              <span className="platform-summary-label">SUSPENDED</span>
            </div>

            <div className="platform-summary-value">
              {formatNumber(businesses?.suspended)}
            </div>

            <div className="platform-summary-footer">
              <span>
                {formatNumber(businesses?.inactive)} inactive businesses
              </span>
            </div>
          </article>
        </section>

        {/*
                    |--------------------------------------------------------------------------
                    | FEATURE SUMMARY
                    |--------------------------------------------------------------------------
                    */}

        <section className="platform-feature-summary-grid">
          <article
            className="platform-clickable-card"
            {...clickableCardProps("/platform/businesses")}
          >
            <div>
              <FiMonitor />
            </div>

            <span>Storefront Enabled</span>

            <strong>{formatNumber(features?.storefrontEnabled)}</strong>
          </article>

          <article
            className="platform-clickable-card"
            {...clickableCardProps("/platform/businesses")}
          >
            <div>
              <FiShoppingBag />
            </div>

            <span>Marketplace Enabled</span>

            <strong>{formatNumber(features?.marketplaceEnabled)}</strong>
          </article>

          <article
            className="platform-clickable-card"
            {...clickableCardProps("/platform/businesses")}
          >
            <div>
              <FiServer />
            </div>

            <span>Active Businesses</span>

            <strong>{formatNumber(businesses?.active)}</strong>
          </article>

          <article
            className="platform-clickable-card"
            {...clickableCardProps("/platform/tenant-users")}
          >
            <div>
              <FiWifi />
            </div>

            <span>Active Tenant Users</span>

            <strong>{formatNumber(users?.active)}</strong>
          </article>
        </section>

        {/*
                    |--------------------------------------------------------------------------
                    | CHART ROW
                    |--------------------------------------------------------------------------
                    */}

        <section className="platform-dashboard-row">
          {/*
                        |--------------------------------------------------------------------------
                        | BUSINESS GROWTH
                        |--------------------------------------------------------------------------
                        */}

          <article className="platform-panel platform-growth-panel">
            <div className="platform-panel-header">
              <div>
                <span className="platform-panel-eyebrow">PLATFORM GROWTH</span>

                <h2>Business Registrations</h2>

                <p>Businesses created during the last 12 months.</p>
              </div>

              <div className="platform-panel-icon">
                <FiTrendingUp />
              </div>
            </div>

            <div className="platform-growth-chart">
              {businessGrowth.length > 0 ? (
                businessGrowth.map((item, index) => {
                  const value = Number(item?.total || 0);

                  const height = Math.max(
                    (value / growthMax) * 100,
                    value > 0 ? 8 : 2,
                  );

                  return (
                    <div
                      className="platform-growth-column"
                      key={`${item?.year}-${item?.month}-${index}`}
                    >
                      <div className="platform-growth-value">{value}</div>

                      <div className="platform-growth-track">
                        <div
                          className="platform-growth-bar"
                          style={{
                            height: `${height}%`,
                          }}
                        />
                      </div>

                      <span>{item?.monthLabel || "-"}</span>
                    </div>
                  );
                })
              ) : (
                <div className="platform-empty-chart">
                  No growth data available.
                </div>
              )}
            </div>
          </article>

          {/*
                        |--------------------------------------------------------------------------
                        | SUBSCRIPTION SUMMARY
                        |--------------------------------------------------------------------------
                        */}

          <article className="platform-panel platform-subscription-panel">
            <div className="platform-panel-header">
              <div>
                <span className="platform-panel-eyebrow">SUBSCRIPTIONS</span>

                <h2>Subscription Status</h2>

                <p>Current business subscription distribution.</p>
              </div>

              <div className="platform-panel-icon">
                <FiBarChart2 />
              </div>
            </div>

            <div className="platform-subscription-total">
              <strong>{formatNumber(subscriptionTotal)}</strong>

              <span>Total subscriptions</span>
            </div>

            <div className="platform-subscription-list">
              {subscriptionSummary.length > 0 ? (
                subscriptionSummary.map((item, index) => {
                  const percentage =
                    subscriptionTotal > 0
                      ? Math.round(
                          (Number(item?.total || 0) / subscriptionTotal) * 100,
                        )
                      : 0;

                  return (
                    <div
                      className="platform-subscription-item"
                      key={`${item?.status}-${index}`}
                    >
                      <div className="platform-subscription-item-heading">
                        <BusinessStatusBadge status={item?.status} />

                        <strong>{formatNumber(item?.total)}</strong>
                      </div>

                      <div className="platform-subscription-progress">
                        <span
                          style={{
                            width: `${percentage}%`,
                          }}
                        />
                      </div>

                      <small>{percentage}% of businesses</small>
                    </div>
                  );
                })
              ) : (
                <div className="platform-empty-state-small">
                  No subscription information found.
                </div>
              )}
            </div>
          </article>
        </section>

        {/*
                    |--------------------------------------------------------------------------
                    | RECENT BUSINESSES
                    |--------------------------------------------------------------------------
                    */}

        <section className="platform-panel platform-business-table-panel">
          <div className="platform-panel-header platform-business-table-header">
            <div>
              <span className="platform-panel-eyebrow">RECENT ACTIVITY</span>

              <h2>Recent Businesses</h2>

              <p>Latest businesses registered on ComKwik.</p>
            </div>

            <button
              type="button"
              className="platform-view-all-button"
              onClick={goToBusinesses}
            >
              Manage Businesses
              <FiArrowRight />
            </button>
          </div>

          <div className="platform-table-wrapper">
            <table className="platform-business-table">
              <thead>
                <tr>
                  <th>Business</th>

                  <th>Code</th>

                  <th>Contact</th>

                  <th>Subscription</th>

                  <th>Features</th>

                  <th>Status</th>

                  <th>Created</th>

                  <th />
                </tr>
              </thead>

              <tbody>
                {recentBusinesses.length > 0 ? (
                  recentBusinesses.map((business) => {
                    const businessId = business?._id || business?.id;

                    return (
                      <tr key={businessId}>
                        <td>
                          <div className="platform-business-cell">
                            <div className="platform-business-avatar">
                              {String(business?.name || "B")
                                .charAt(0)
                                .toUpperCase()}
                            </div>

                            <div>
                              <strong>{business?.name || "-"}</strong>

                              <span>
                                {business?.legalName ||
                                  business?.subdomain ||
                                  "-"}
                              </span>
                            </div>
                          </div>
                        </td>

                        <td>
                          <span className="platform-business-code">
                            {business?.code || "-"}
                          </span>
                        </td>

                        <td>
                          <div className="platform-contact-cell">
                            <strong>{business?.mobile || "-"}</strong>

                            <span>{business?.email || "-"}</span>
                          </div>
                        </td>

                        <td>
                          <BusinessStatusBadge
                            status={business?.subscriptionStatus}
                          />
                        </td>

                        <td>
                          <BusinessFeatureTags
                            storefrontEnabled={business?.storefrontEnabled}
                            marketplaceEnabled={business?.marketplaceEnabled}
                          />
                        </td>

                        <td>
                          <BusinessStatusBadge status={business?.status} />
                        </td>

                        <td>{formatDate(business?.createdAt)}</td>

                        <td>
                          <button
                            type="button"
                            className="platform-row-action"
                            title="View business"
                            onClick={() =>
                              history.push(`/platform/businesses/${businessId}`)
                            }
                          >
                            <FiExternalLink />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={8}>
                      <div className="platform-empty-table">
                        <FiBriefcase />

                        <strong>No businesses found</strong>

                        <span>Registered businesses will appear here.</span>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        {/*
                    |--------------------------------------------------------------------------
                    | FOOTER
                    |--------------------------------------------------------------------------
                    */}

        <footer className="platform-dashboard-footer">
          <span>ComKwik Platform Administration</span>

          <span>Secure Platform Super Admin Session</span>
        </footer>
      </div>
  );
};

export default PlatformDashboard;
